import React, { FC, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@clearcover/atlas';

import {
  AnimatedScreen,
  Column,
  HRule,
  Row,
  TableProps,
} from 'Shared/components';
import { PartnerContext } from 'Admin/utils';

import {
  Details,
  AgencyDetails,
  AgentDetails,
  DirectionalCard,
  DirectionalCardContainer,
  buildPolicyListRow,
  PoliciesTable,
} from '../../components';
import { useReattributionRedirect } from '../../hooks';
import { useStore } from '../../store';

import styles from '../styles.module.css'; // Shared styles from reattribution screens
import successStyles from './styles.module.css'; // success specific styles

export const Success: FC = () => {
  const { currentPartner } = useContext(PartnerContext);
  const history = useHistory();
  const [state, dispatch] = useStore();
  const [temporaryState, setTemporaryState] = useState<typeof state>();
  const canViewRoute = useReattributionRedirect();

  useEffect(() => {
    // set temporary state for initial display and reset the reducer
    setTemporaryState(state);
    dispatch({
      type: 'reattribution.reset',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentPartner || !temporaryState || !canViewRoute) return null;

  return (
    <AnimatedScreen testId="partner-reattribution-success-page">
      <h1>Reattribution Complete</h1>
      <HRule size="lg" />
      <h3>Reattribution Details</h3>
      <DirectionalCardContainer layout="horizontal">
        <DirectionalCard>
          <div className={styles.cardHeading}>Policies From:</div>
          <HRule />
          <Row className={successStyles.cardRow}>
            <Column>
              <AgencyDetails agency={temporaryState.from.agency} />
            </Column>
          </Row>
          <Row className={successStyles.cardRow}>
            <Column>
              <AgentDetails agent={temporaryState.from.agent} />
            </Column>
          </Row>
        </DirectionalCard>
        <DirectionalCard>
          <div className={styles.cardHeading}>Policies To:</div>
          <HRule />
          <Row className={successStyles.cardRow}>
            <Column>
              <AgencyDetails agency={temporaryState.to.agency} />
            </Column>
          </Row>
          <Row className={successStyles.cardRow}>
            <Column>
              <AgentDetails agent={temporaryState.to.agent} />
            </Column>
          </Row>
          {temporaryState.to.effectiveDate && (
            <Row className={successStyles.cardRow}>
              <Column>
                <Details
                  heading="Reattribution Effective Date"
                  content={temporaryState.to.effectiveDate}
                />
              </Column>
            </Row>
          )}
        </DirectionalCard>
      </DirectionalCardContainer>
      <h3>Reattributed Policy Details</h3>
      <PoliciesTable
        checkboxSelection={false}
        name="reattribution-success-table"
        rows={temporaryState.policies.data.reduce(
          (acc: TableProps['rows'], row) => {
            const selectedOnListScreen =
              temporaryState.policies.selections.includes(
                row.universalPolicyId
              );

            if (selectedOnListScreen) {
              acc.push(
                buildPolicyListRow(
                  {
                    ...row,
                    // apply the new attributed details to the list
                    attributedPartnerName: temporaryState.to.agency?.name || '',
                    attributedPortalUserEmail:
                      temporaryState.to.agent?.email || null,
                    attributedPortalUserName:
                      temporaryState.to.agent?.name || null,
                  },
                  false
                )
              );
            }
            return acc;
          },
          []
        )}
      />
      <div className={successStyles.backButtonContainer}>
        <Button
          type="button"
          data-testid="reattribution-success-back"
          onClick={() => {
            history.push(`/admin/partners/${currentPartner.id}/reattribution`);
          }}
        >
          Return to Policy Reattribution
        </Button>
      </div>
    </AnimatedScreen>
  );
};
